import { Box, makeStyles } from "@material-ui/core";
import React from "react";
import { useHistory } from "react-router";

import { Agent } from "@/api/generated";
import { homeCardStyle, HomeRail, HomeSection } from "@/components/home/HomeSection";
import { FeaturedPlannerCard } from "@/components/planner_onboarding/PlannerCards";
import { usePlannerReducedMotion } from "@/components/planner_onboarding/PlannerReveal";
import { RecommendedPlannerCarousel } from "@/components/planner_onboarding/RecommendedPlannerCarousel";
import { layoutTokens } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";

// PC は限定物件・動画と同じ横スクロールのレールに、登録導線と同じ大判カードを並べる（Figma 案_PC_ホーム）
const PC_CARD_WIDTH = 320;
// 取得中に確保する高さ。写真・スコア帯・コメント・ボタンを含むカードの概ねの高さ
const SKELETON_HEIGHT = 430;

const useStyles = makeStyles({
  // SP は登録導線のカルーセルをそのまま使う。レール自身が左右 24px の余白を持つので、
  // ホームの余白（sectionStack の 24px）を打ち消して画面端まで使う
  carouselBleed: { margin: `0 -${layoutTokens.space[24]}px` },
  // 枠に幅を持たせ、カードを枠いっぱいに伸ばして3枚の高さを揃える
  pcCard: { width: PC_CARD_WIDTH, display: "flex", "& > article": { flex: 1 } },
  skeletonCard: { ...homeCardStyle, width: "100%", height: SKELETON_HEIGHT },
  pcSkeletonCard: { width: PC_CARD_WIDTH },
});

const TITLE = { sp: "相性の良いプランナー", pc: "あなたに相性の良いプランナー" };
const SUBTITLE = "診断の結果をもとに、相性の高い順に並べています。";

/**
 * ホームの「相性の良いプランナー」。登録導線（診断結果）と同じ大判カードを、相性グラフを省いて出す。
 * SP は登録導線と同じカルーセル、PC は他セクションと同じ横スクロールのレール。
 */
export const CompatibilityAgentsSection: React.FC<{ agents: Agent[]; onClickAgent: (agentId: number, rank: number) => void }> = ({
  agents,
  onClickAgent,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const reduced = usePlannerReducedMotion();
  const history = useHistory();
  if (agents.length === 0) return null;
  // 順位（王冠）は API の並び順そのまま。詳細画面にも引き継ぐ
  const openDetail = (agent: Agent) => {
    if (agent.id != null) onClickAgent(agent.id, agents.indexOf(agent) + 1);
  };
  return (
    <HomeSection
      title={isMobile ? TITLE.sp : TITLE.pc}
      subtitle={isMobile ? SUBTITLE : undefined}
      onViewAll={() => history.push("/agent_search")}
    >
      {isMobile ? (
        <Box className={classes.carouselBleed}>
          <RecommendedPlannerCarousel agents={agents} reduced={reduced} showRadarChart={false} onDetail={openDetail} />
        </Box>
      ) : (
        <HomeRail label="相性の良いプランナー">
          {agents.map((agent, index) => (
            <Box key={agent.id ?? `missing-${index}`} className={classes.pcCard}>
              <FeaturedPlannerCard agent={agent} rank={index + 1} showRadarChart={false} onDetail={() => openDetail(agent)} />
            </Box>
          ))}
        </HomeRail>
      )}
    </HomeSection>
  );
};

export const CompatibilityAgentsSectionSkeleton: React.FC = () => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  return (
    <HomeSection title={isMobile ? TITLE.sp : TITLE.pc} subtitle={isMobile ? SUBTITLE : undefined}>
      <div aria-label="相性の良いプランナーを読み込み中" aria-busy="true">
        {isMobile ? (
          <Box className={classes.skeletonCard} />
        ) : (
          <HomeRail label="読み込み中のプランナー" arrows={false}>
            {Array.from({ length: 3 }, (_, index) => (
              <Box key={index} className={`${classes.skeletonCard} ${classes.pcSkeletonCard}`} />
            ))}
          </HomeRail>
        )}
      </div>
    </HomeSection>
  );
};
